import AegisV2 from '@tencent/aegis-web-sdk-v2';
import {
  resolvePromise,
  showCaptcha,
  TCaptchaConfigs,
  TCaptchaVerification,
} from '@tencent/oneid-utils';
import message from 'common/components/Message';
import { CAPTCHA_CANCEL, CAPTCHA_ERROR } from 'common/constants/Constants';
import i18n from 'common/locales';
import { dataReport } from 'common/utils/dataReport';

/** 滑块验证埋点事件（公共事件名，通过私参区分场景） */
const CAPTCHA_REPORT_EVENTS = {
  exposure: 'e#oneid_security_verification_web#all#exposure',
  close: 'e#oneid_security_verification_web#close#click',
  submit: 'e#oneid_security_verification_web#submit#click',
} as const;

/** 滑块上报参数，由业务调用方传入公参和私参 */
export interface ICaptchaReportParams {
  /** 公参（由子应用 Store 提供） */
  globalParams: Record<string, any>;
  /** 私参（事件特有参数，如 verify_type，用于区分不同场景） */
  privateParams?: Record<string, any>;
  /** 上报选项 */
  reportOptions?: Record<string, any>;
}

/**
 * 执行滑块埋点上报
 * @param eventName 上报事件标识
 * @param reportParams 上报参数（含公参、私参）
 * @param extraParams 额外的私参（如 submit_result）
 */
const reportCaptchaEvent = (
  eventName: string,
  reportParams: ICaptchaReportParams,
  extraParams?: Record<string, any>,
) => {
  const { globalParams, privateParams, reportOptions } = reportParams;
  dataReport(eventName, globalParams, { ...privateParams, ...extraParams }, reportOptions);
};

/**
 * 利用滑块ready方法进行曝光上报方法，并合并到滑块配置中
 * @param configs 外部传入的 TCaptchaConfigs
 * @param reportParams 上报参数
 * @returns 合并后的 configs
 */
const mergeCaptchaConfigs = (
  configs?: TCaptchaConfigs,
  reportParams?: ICaptchaReportParams,
): TCaptchaConfigs | undefined => {
  if (!reportParams) return configs;

  const reportReady = () => {
    reportCaptchaEvent(CAPTCHA_REPORT_EVENTS.exposure, reportParams);
  };

  if (!configs) {
    return { ready: reportReady };
  }

  const originalReady = configs.ready;
  return {
    ...configs,
    ready: () => {
      reportReady();
      originalReady?.();
    },
  };
};

const captchaRequest = async (
  request: (captchaVerification?: TCaptchaVerification) => Promise<any>,
  reportParams?: ICaptchaReportParams,
  configs?: TCaptchaConfigs,
) => {
  const requestApi = request();
  const [err, res] = await resolvePromise(requestApi);
  if (err || !res || res.errCode) return requestApi;

  const successType = res.next?.type;
  // 没触发滑块验证，直接返回数据
  if (successType !== 'CAPTCHA_OPTIONS') return requestApi;

  // 触发了滑块验证的场景
  try {
    const captchaOptions = res.next?.captchaOptions?.options;

    // 合并 configs，将曝光上报注入 ready 回调
    const mergedConfigs = mergeCaptchaConfigs(configs, reportParams);

    const { captchaVerification, errorLog } = await showCaptcha(captchaOptions, mergedConfigs);

    // 如果期间存在报错，需要进行上报，方便问题统计与定位
    if (errorLog.length) {
      window.aegis?.report({
        type: 'normal',
        level: AegisV2.logLevel.CUSTOM_ERROR,
        name: CAPTCHA_ERROR,
        message: '用户成功加载验证码，但是期间出现过错误',
        count: errorLog.length,
        errorLog,
      });
    }
    // 用户成功通过验证码
    if (captchaVerification) {
      const result = await request(captchaVerification);
      // 滑块提交上报（成功/失败）
      if (reportParams) {
        reportCaptchaEvent(CAPTCHA_REPORT_EVENTS.submit, reportParams, {
          submit_result: result?.errCode ? '1' : '0',
        });
      }
      return result;
    }
    // 用户主动关闭滑块
    if (reportParams) {
      reportCaptchaEvent(CAPTCHA_REPORT_EVENTS.close, reportParams);
    }
    return Promise.reject({
      data: { errCode: CAPTCHA_CANCEL, errMessage: i18n.t('滑块验证码关闭') },
    });
  } catch (err) {
    const { errCode, errMessage, ...rest } = err.data || {};
    // 外侧的验证码错误类型无错误提示，需要在内部处理
    if (errCode === CAPTCHA_ERROR) {
      errMessage && message.error(errMessage);
      window.aegis?.error({
        type: 'normal',
        level: AegisV2.logLevel.CUSTOM_ERROR,
        name: errCode,
        message: errMessage,
        ...rest,
      });
    }
    // 滑块提交上报（异常情况）
    if (reportParams) {
      reportCaptchaEvent(CAPTCHA_REPORT_EVENTS.submit, reportParams, {
        submit_result: '1',
      });
    }
    // 外部需要对滑块错误进行业务层面处理
    return Promise.reject(err);
  }
};

export const newCaptchaRequest = async (
  request: (captchaVerification?: TCaptchaVerification) => Promise<any>,
  reportParams?: ICaptchaReportParams,
  configs?: TCaptchaConfigs,
) => {
  const requestApi = request();
  const [err, res] = await resolvePromise(requestApi);
  if (err || !res || res.errCode) return requestApi;

  // 没触发滑块验证，且发送成功，直接返回数据
  if (!res.captcha && res.state_token) return requestApi;

  // 触发了滑块验证的场景
  try {
    const captchaOptions = res.captcha;

    // 合并 configs，将曝光上报注入 ready 回调
    const mergedConfigs = mergeCaptchaConfigs(configs, reportParams);

    const { captchaVerification, errorLog } = await showCaptcha(captchaOptions, mergedConfigs);

    // 如果期间存在报错，需要进行上报，方便问题统计与定位
    if (errorLog.length) {
      window.aegis?.report({
        type: 'info',
        level: AegisV2.logLevel.CUSTOM_ERROR,
        name: CAPTCHA_ERROR,
        message: '用户成功加载验证码，但是期间出现过错误',
        count: errorLog.length,
        errorLog,
      });
    }
    // 用户成功通过验证码
    if (captchaVerification) {
      return request(captchaVerification);
    }
    return Promise.reject({
      data: { errCode: CAPTCHA_CANCEL, errMessage: i18n.t('滑块验证码关闭') },
    });
  } catch (err) {
    const { errCode, errMessage, ...rest } = err.data || {};
    // 外侧的验证码错误类型无错误提示，需要在内部处理
    if (errCode === CAPTCHA_ERROR) {
      errMessage && message.error(errMessage);
      window.aegis?.error({
        type: 'info',
        level: AegisV2.logLevel.CUSTOM_ERROR,
        name: errCode,
        message: errMessage,
        ...rest,
      });
    }
    // 滑块提交上报（异常情况）
    if (reportParams) {
      reportCaptchaEvent(CAPTCHA_REPORT_EVENTS.submit, reportParams, {
        submit_result: '1',
      });
    }
    // 外部需要对滑块错误进行业务层面处理
    return Promise.reject(err);
  }
};

export default captchaRequest;
